import { lazy, Suspense } from 'react';
const RentalPaymentResult = lazy(() => import('./pages/RentalPaymentResult'));
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { DataCacheProvider } from './contexts/DataCacheContext';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import { Toaster } from './components/ui/sonner';
import AuthPage from './pages/AuthPage';
import AuthCallback from './components/AuthCallback';
import LandingPage from './pages/LandingPage';
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const POSPage = lazy(() => import('./pages/POSPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const CashPage = lazy(() => import('./pages/CashPage'));
const ClosingPage = lazy(() => import('./pages/ClosingPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const BusinessCardPage = lazy(() => import('./pages/BusinessCardPage'));
const InvoicePage = lazy(() => import('./pages/InvoicePage'));
const SalesHistoryPage = lazy(() => import('./pages/SalesHistoryPage'));
const BillsPage = lazy(() => import('./pages/BillsPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PayrollPage = lazy(() => import('./pages/PayrollPage'));
const PurchasesPage = lazy(() => import('./pages/PurchasesPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const FacturasPage = lazy(() => import('./pages/FacturasPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const InstallPage = lazy(() => import('./pages/InstallPage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const FinancialReportsPage = lazy(() => import('./pages/FinancialReportsPage'));
const RealEstatePage = lazy(() => import('./pages/RealEstatePage'));
const LeadsPage = lazy(() => import('./pages/LeadsPage'));
const NotesPage = lazy(() => import('./pages/NotesPage'));
const PriceIndexPage = lazy(() => import('./pages/PriceIndexPage'));
const BudgetPage = lazy(() => import('./pages/BudgetPage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const FinancialToolsPage = lazy(() => import('./pages/FinancialToolsPage'));
const CashFlowPage = lazy(() => import('./pages/CashFlowPage'));
const SalesGoalsPage = lazy(() => import('./pages/SalesGoalsPage'));
const ExpenseAnalysisPage = lazy(() => import('./pages/ExpenseAnalysisPage'));
const ComparativePage = lazy(() => import('./pages/ComparativePage'));
const ProfitabilityPage = lazy(() => import('./pages/ProfitabilityPage'));
const ProjectionsPage = lazy(() => import('./pages/ProjectionsPage'));
const AnalysisPage = lazy(() => import('./pages/AnalysisPage'));
const AppointmentsPage = lazy(() => import('./pages/AppointmentsPage'));
const TransportPage = lazy(() => import('./pages/TransportPage'));
const CompaniesPage = lazy(() => import('./pages/CompaniesPage'));
const ToolRelationshipsPage = lazy(() => import('./pages/ToolRelationshipsPage'));
const PlayStoreGuidePage = lazy(() => import('./pages/PlayStoreGuidePage'));
const CajaRegistradoraPage = lazy(() => import('./pages/CajaRegistradoraPage'));
const ArqueoPage = lazy(() => import('./pages/ArqueoPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const ExternalSignPage = lazy(() => import('./pages/ExternalSignPage'));
const ClientPortalPage = lazy(() => import('./pages/ClientPortalPage'));
const PublicCardPage = lazy(() => import('./pages/PublicCardPage'));
const KioskPage = lazy(() => import('./pages/KioskPage'));
const SignPaystubPage = lazy(() => import('./pages/SignPaystubPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const CobrosPage = lazy(() => import('./pages/CobrosPage'));
const PublicPromotionPage = lazy(() => import('./pages/PublicPromotionPage'));
const AdminRevenuePage = lazy(() => import('./pages/AdminRevenuePage'));
import { PaySuccessPage, PayCancelPage } from './pages/PaySuccessPage';
import './App.css';
import './styles/refresh.css';
import './styles/workspace-current.css';

function PrivateRoute({ children }) {
  const { user, bootstrapped } = useAuth();
  
  if (!bootstrapped) {
    return (
      <div className="bc-app-loading min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/" />;
}

function PublicRoute({ children }) {
  const { user, bootstrapped } = useAuth();
  
  if (!bootstrapped) {
    return (
      <div className="bc-app-loading min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" /> : children;
}

function AppRoutes() {
  const location = useLocation(); // read hash from here (reactive), not window.location.hash
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/sign/:token" element={<ExternalSignPage />} />
      <Route path="/portal/:token" element={<ClientPortalPage />} />
      <Route path="/card/:share_token" element={<PublicCardPage />} />
      <Route path="/kiosk/:code" element={<KioskPage />} />
      <Route path="/sign-paystub/:token" element={<SignPaystubPage />} />
      <Route path="/instalar" element={<InstallPage />} />
      <Route path="/catalogo/:token" element={<CatalogPage />} />
      <Route path="/dashboard" element={
        <PrivateRoute>
          <Layout><DashboardPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/pos" element={
        <PrivateRoute>
          <Layout><POSPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/inventory" element={
        <PrivateRoute>
          <Layout><InventoryPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/cash" element={
        <PrivateRoute>
          <Layout><CashPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/caja" element={
        <PrivateRoute>
          <Layout><CajaRegistradoraPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/closing" element={
        <PrivateRoute>
          <Layout><ClosingPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/arqueo" element={
        <PrivateRoute>
          <Layout><ArqueoPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/settings" element={
        <PrivateRoute>
          <Layout><SettingsPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/business-card" element={
        <PrivateRoute>
          <Layout><BusinessCardPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/bills" element={
        <PrivateRoute>
          <Layout><BillsPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/tasks" element={
        <PrivateRoute>
          <Layout><TasksPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/pricing" element={
        <PrivateRoute>
          <Layout><PricingPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/sales-history" element={
        <PrivateRoute>
          <Layout><SalesHistoryPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/invoice/:saleId" element={
        <PrivateRoute>
          <Layout><InvoicePage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/admin" element={
        <PrivateRoute>
          <Layout><AdminPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/payroll" element={
        <PrivateRoute>
          <Layout><PayrollPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/purchases" element={
        <PrivateRoute>
          <Layout><PurchasesPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/customers" element={
        <PrivateRoute>
          <Layout><CustomersPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/contacts" element={
        <PrivateRoute>
          <Layout><ContactsPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/facturas" element={
        <PrivateRoute>
          <Layout><FacturasPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/chat" element={
        <PrivateRoute>
          <Layout><ChatPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/reports" element={
        <PrivateRoute>
          <Layout><FinancialReportsPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/rent-payment-result" element={<RentalPaymentResult/>}/>
      <Route path="/realestate" element={
        <PrivateRoute>
          <Layout><RealEstatePage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/leads" element={
        <PrivateRoute>
          <Layout><LeadsPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/notes" element={
        <PrivateRoute>
          <Layout><NotesPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/price-index" element={
        <PrivateRoute>
          <Layout><PriceIndexPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/budget" element={
        <PrivateRoute>
          <Layout><BudgetPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/news" element={
        <PrivateRoute>
          <Layout><NewsPage /></Layout>
        </PrivateRoute>
      } />
      {/* Financial Tools Routes */}
      <Route path="/finanzas" element={
        <PrivateRoute>
          <Layout><FinancialToolsPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/finanzas/flujo-caja" element={
        <PrivateRoute>
          <Layout><CashFlowPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/finanzas/metas" element={
        <PrivateRoute>
          <Layout><SalesGoalsPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/finanzas/gastos" element={
        <PrivateRoute>
          <Layout><ExpenseAnalysisPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/finanzas/comparativo" element={
        <PrivateRoute>
          <Layout><ComparativePage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/finanzas/rentabilidad" element={
        <PrivateRoute>
          <Layout><ProfitabilityPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/finanzas/proyecciones" element={
        <PrivateRoute>
          <Layout><ProjectionsPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/finanzas/analisis" element={
        <PrivateRoute>
          <Layout><AnalysisPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/appointments" element={
        <PrivateRoute>
          <Layout><AppointmentsPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/transport" element={
        <PrivateRoute>
          <Layout><TransportPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/services" element={
        <PrivateRoute>
          <Layout><ServicesPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/cobros" element={
        <PrivateRoute>
          <Layout><CobrosPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/admin/revenue" element={
        <PrivateRoute>
          <Layout><AdminRevenuePage /></Layout>
        </PrivateRoute>
      } />
      {/* Public post-payment landing pages — customers paying don't have a Briefcase account */}
      <Route path="/promo/:token" element={<PublicPromotionPage />} />
      <Route path="/pay/success" element={<PaySuccessPage />} />
      <Route path="/pay/cancel" element={<PayCancelPage />} />
      <Route path="/companies" element={
        <PrivateRoute>
          <Layout><CompaniesPage /></Layout>
        </PrivateRoute>
      } />
      <Route path="/tool-relationships" element={
        <PrivateRoute>
          <Layout><ToolRelationshipsPage /></Layout>
        </PrivateRoute>
      } />
      {/* Public routes */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <DataCacheProvider>
              <Suspense fallback={<div role="status" aria-live="polite" className="min-h-screen flex items-center justify-center">Cargando pantalla…</div>}><AppRoutes /></Suspense>
              <Toaster position="top-right" richColors closeButton />
            </DataCacheProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
